// types and components shared between the question block variants
import * as React from 'react';

import { WrappedMessage } from 'utils';
import { Icon, Button } from 'ui/components';
import messages from '../../displayMessages';

// NOTE: these types must be kept in sync with
// what is returned from labxchange-xblocks/question_block.py::QuestionBlock.student_view_user_state()
export interface Option {
    content: string;
    checked: boolean;
    comment: string;
}

export interface OptionResponseAnswer {
    index?: number;
}

export interface OptionResponse {
    type: 'optionresponse';
    question: string;
    display: 'radio'|'dropdown';
    options: Option[];
    studentAnswer: OptionResponseAnswer;
}

export interface StringResponseAnswer {
    response?: string;
}

export interface StringResponse {
    type: 'stringresponse';
    question: string;
    studentAnswer: StringResponseAnswer;
    comment?: string;
    answer?: string;
}

export interface ChoiceResponseAnswer {
    selected?: number[];
}

export interface ChoiceResponse {
    type: 'choiceresponse';
    question: string;
    choices: Option[];
    studentAnswer: ChoiceResponseAnswer;
    comment: string;
}

export type QuestionData = OptionResponse|StringResponse|ChoiceResponse;

export interface Hint {
    content: string;
}

export interface ViewData<T> {
    questionData: T;
    maxAttempts: number;
    current_score: number;
    total_possible: number;
    hints: Hint[];
    studentAttempts: number;
    correct?: boolean;
}

interface HintsProps {
    hints: Hint[];
}

interface HintsState {
    [index: number]: boolean;
}


export class Hints extends React.PureComponent<HintsProps, HintsState> {
    constructor(props: HintsProps) {
        super(props);
        this.state = {};
    }

    public render(): React.ReactNode {
        const hints = this.props.hints.map((hint, i) => {
            const open = this.state[i];
            return (
                <div className='hint'>
                    <button className='hint-title' onClick={() => this.toggle(i)}>
                        <span>
                            {this.props.hints.length > 1
                                ? <WrappedMessage
                                    message={messages.hint}
                                    values={{ n: i+1 }}
                                />
                                : <WrappedMessage
                                    message={messages.hintOne}
                                />
                            }
                        </span>
                        {open ? <Icon name='chevron-up' /> : <Icon name='chevron-down' />}
                    </button>
                    {this.state[i] === true &&
                        <div className='hint-content'>{hint.content}</div>
                    }
                </div>
            );
        });

        return (
            <div className='hints'>
                {hints}
            </div>
        );
    }

    private toggle(i: number) {
        this.setState({ [i]: !this.state[i] });
    }
}

interface FeedbackProps {
    max: number;
    used: number;
    correct?: boolean;
    answer?: string;
}

export const Feedback: React.SFC<FeedbackProps> = ({ max, used, correct, answer }) => {
    if (correct === true) {
        return (
            <div className='general-feedback correct'>
                <Icon name='alert-correct-hex' />
                <WrappedMessage message={messages.questionCorrect} />
            </div>
        );
    } else if (correct === false && max > 0 && used < max) {
        return (
            <div className='general-feedback info'>
                <Icon name='alert-info-hex' />
                <WrappedMessage
                    message={messages.questionIncorrectAttempts}
                    values={{ remaining: max - used }}
                />
            </div>
        );
    } else if (correct === false && answer) {
        return (
            <div className='general-feedback incorrect'>
                <Icon name='alert-fail-hex' />
                <WrappedMessage message={messages.questionIncorrectWithAnswer} values={{ answer }} />
            </div>
        );
    } else if (correct === false && max > 0 && used >= max) {
        return (
            <div className='general-feedback incorrect'>
                <Icon name='alert-fail-hex' />
                <WrappedMessage
                    message={messages.questionIncorrectAttempts}
                    values={{ remaining: 0 }}
                />
            </div>
        );
    } else if (correct === false) {
        return (
            <div className='general-feedback incorrect'>
                <Icon name='alert-fail-hex' />
                <WrappedMessage message={messages.questionIncorrect} />
            </div>
        );
    } else {
        return null;
    }
};

interface SpecificFeedbackProps {
    comment?: string;
    correct?: boolean;
}

export const SpecificFeedback: React.SFC<SpecificFeedbackProps> = ({ comment, correct }) => {
    if (correct === true && comment) {
        return (
            <div className='feedback'>
                <WrappedMessage message={messages.correctFeedback} values={{ comment }} />
            </div>
        );
    } else if (correct === false && comment) {
        return (
            <div className='feedback'>
                <WrappedMessage message={messages.incorrectFeedback} values={{ comment }} />
            </div>
        );
    } else {
        return null;
    }
};

interface SubmitProps {
    hidden: boolean;
    disabled: boolean;
    maxAttempts: number;
    usedAttempts: number;
    onSubmit: () => void;
}

export const Submit: React.SFC<SubmitProps> = (props) => (
    <div className='submit-content'>
        {props.hidden
            ? <div className='spacer' />
            : <Button
                label={messages.submitAnswer}
                btnStyle='primary'
                disabled={props.disabled}
                onClick={() => props.onSubmit()}
            />
        }
        {props.maxAttempts > 0 &&
            <span className='attempts'>
                <WrappedMessage
                    message={messages.questionAttemptsLeft}
                    values={{
                        attempts: Math.max(props.maxAttempts - props.usedAttempts, 0),
                    }}
                />
            </span>
        }
    </div>
);

export const noMoreSubmissions = (data: ViewData<QuestionData>): boolean => {
    // return true if the question will allow no more submissions
    return data.correct || (data.maxAttempts > 0 && data.studentAttempts >= data.maxAttempts);
};
